import { useCallback } from 'react';
import { doc, updateDoc, setDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { FullProject } from '../../types';
import confetti from 'canvas-confetti';
import { areProjectIdsMatching } from '../../utils/idNormalizer';

interface UseProjectTemplatesProps {
  allProjects: FullProject[];
  setAllProjects: React.Dispatch<React.SetStateAction<FullProject[]>>;
  allProjectsRef: React.MutableRefObject<FullProject[]>;
  currentProjectIdRef: React.MutableRefObject<string>;
  setCurrentProjectId: (id: string) => void;
  userId: string | null | undefined;
  tenantId: string;
  COMM_PROJECT_ID: string;
  INBOX_PROJECT_ID: string;
  deleteProjectFromPersistence: (projectId: string) => Promise<void>;
}

export function useProjectTemplates({
  allProjects,
  setAllProjects,
  allProjectsRef,
  currentProjectIdRef,
  setCurrentProjectId,
  userId,
  tenantId,
  COMM_PROJECT_ID,
  INBOX_PROJECT_ID,
  deleteProjectFromPersistence
}: UseProjectTemplatesProps) {

  const deleteSampleData = useCallback(async () => {
    // 1. Find all projects marked as sample
    const sampleProjects = allProjectsRef.current.filter(p => p.isSample || p.metadata?.isSample);
    if (sampleProjects.length === 0) return;

    // 2. Set sampleDismissed in the inbox project metadata
    const inboxProject = allProjectsRef.current.find(p => p.id === INBOX_PROJECT_ID);
    if (inboxProject) {
      const updatedInbox = {
        ...inboxProject,
        metadata: {
          ...(inboxProject.metadata || {}),
          sampleDismissed: true
        },
        lastModified: Date.now()
      };
      
      setAllProjects(prev => prev.map(p => p.id === INBOX_PROJECT_ID ? updatedInbox : p));
      
      // Update in Firestore
      try {
        await updateDoc(doc(db, 'projects', INBOX_PROJECT_ID), {
          'metadata.sampleDismissed': true,
          lastModified: Date.now()
        });
      } catch (e) {
        console.error('Failed to update inbox project metadata:', e);
      }
    }
    
    // 3. Delete the sample projects and their tasks
    for (const p of sampleProjects) {
      // Immediately update state
      setAllProjects(prev => prev.filter(item => item.id !== p.id));
      // Background sync: Purge from databases
      await deleteProjectFromPersistence(p.id);
    }
    
    // 3b. Also remove sample contacts from COMM_PROJECT_ID if present and mark sampleDismissed
    const isComm = (id: string) => id === COMM_PROJECT_ID || id === 'comm_internal' || areProjectIdsMatching(id, COMM_PROJECT_ID);
    const commProject = allProjectsRef.current.find(p => isComm(p.id));
    if (commProject) {
      const isSampleContactTask = (t: { id: string | number }) =>
        String(t.id).startsWith('sample_contact_') || String(t.id).startsWith('person_sample_contact_');
      const sampleContactTasks = (commProject.tasks || []).filter(isSampleContactTask);
      const remainingTasks = (commProject.tasks || []).filter(t => !isSampleContactTask(t));
      const updatedComm = { 
        ...commProject, 
        tasks: remainingTasks, 
        metadata: { ...(commProject.metadata || {}), sampleDismissed: true },
        lastModified: Date.now() 
      };
      setAllProjects(prev => prev.map(p => isComm(p.id) ? updatedComm : p));
      
      if (sampleContactTasks.length > 0) {
        try {
          await Promise.all(sampleContactTasks.map(t => deleteDoc(doc(db, 'tasks', t.id.toString()))));
        } catch (e) {
          console.error('Failed to delete sample contacts from comm project:', e);
        }
      }

      try {
        await updateDoc(doc(db, 'projects', COMM_PROJECT_ID), {
          'metadata.sampleDismissed': true,
          lastModified: Date.now()
        });
      } catch (e) {
        console.error('Failed to update comm project metadata:', e);
      }
    }

    if (typeof localStorage !== 'undefined') {
      localStorage.setItem('gwantt_sample_dismissed', 'true');
    }
    
    // 4. Reset active project ID if we deleted it
    if (sampleProjects.some(p => p.id === currentProjectIdRef.current)) {
      const remainingProjects = allProjectsRef.current.filter(p => 
        !sampleProjects.some(sp => sp.id === p.id) &&
        !areProjectIdsMatching(p.id, INBOX_PROJECT_ID) && 
        !isComm(p.id)
      );
      const nextId = remainingProjects[0]?.id || 'globalKanban';
      currentProjectIdRef.current = nextId;
      setCurrentProjectId(nextId);
    }
  }, [INBOX_PROJECT_ID, COMM_PROJECT_ID, deleteProjectFromPersistence, setAllProjects, allProjectsRef, currentProjectIdRef, setCurrentProjectId]);

  const updateSampleProjectForBranch = useCallback(async (branchId: string) => {
    if (!userId) return;
    const sampleProjId = `sample_${userId}`;
    const { getSampleDataForBranch } = await import('../../utils/projectTemplates');
    const data = getSampleDataForBranch(branchId, userId, tenantId, sampleProjId);

    // 1. Delete existing tasks for sample project
    const sampleProj = allProjects.find(p => p.id === sampleProjId);
    if (sampleProj) {
      try {
        const oldTaskIds = (sampleProj.tasks || []).map(t => t.id.toString());
        await Promise.all(oldTaskIds.map(tid => deleteDoc(doc(db, 'tasks', tid))));
      } catch (e) {
        console.error('Failed to delete old sample tasks:', e);
      }
    }

    // 2. Save new sample project doc
    try {
      await setDoc(doc(db, 'projects', sampleProjId), {
        name: data.name,
        color: 'bg-indigo-600',
        workspace: 'professional',
        userId: userId,
        tenantId: tenantId,
        isSample: true,
        categoryId: 'cat_strategy',
        metadata: data.metadata,
        lastModified: Date.now(),
        changelog: []
      });
    } catch (e) {
      console.error('Failed to save updated sample project doc:', e);
    }

    // 3. Save new tasks
    try {
      await Promise.all(data.tasks.map(task => setDoc(doc(db, 'tasks', task.id.toString()), task)));
    } catch (e) {
      console.error('Failed to save new sample tasks:', e);
    }

    // 4. Update the inbox metadata
    const inboxId = `inbox_inforum_${userId}`;
    const inboxProj = allProjects.find(p => p.id === inboxId);
    if (inboxProj) {
      const updatedMeta = {
        ...(inboxProj.metadata || {}),
        onboardingCompleted: true,
        onboardingBranch: branchId
      };
      try {
        await updateDoc(doc(db, 'projects', inboxId), {
          metadata: updatedMeta,
          lastModified: Date.now()
        });
      } catch (e) {
        console.error('Failed to update inbox metadata:', e);
      }
    }

    // 5. Update local state
    setAllProjects(prev => {
      return prev.map(p => {
        if (p.id === sampleProjId) {
          return {
            ...p,
            name: data.name,
            categoryId: 'cat_strategy',
            metadata: data.metadata,
            tasks: data.tasks,
            lastModified: Date.now()
          };
        }
        if (p.id === inboxId) {
          return {
            ...p,
            metadata: {
              ...(p.metadata || {}),
              onboardingCompleted: true,
              onboardingBranch: branchId
            },
            lastModified: Date.now()
          };
        }
        return p;
      });
    });

    try {
      confetti({
        particleCount: 80,
        spread: 70,
        origin: { y: 0.6 }
      });
    } catch {}
  }, [userId, tenantId, allProjects, setAllProjects]);

  return {
    deleteSampleData,
    updateSampleProjectForBranch
  };
}
